"use client";

import React, { ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";

interface BaseHeaderProps {
  /** Custom background and border styling */
  variant?: "default" | "editor";
  /** Left side content - can be logo, back button + title, etc. */
  leftContent: ReactNode;
  /** Right side content - typically credits and account buttons */
  rightContent: ReactNode;
  /** Additional CSS classes for the header */
  className?: string;
}

/**
 * BaseHeader - Foundation component for all headers
 * Provides consistent layout structure and responsive behavior
 */
export default function BaseHeader({
  variant = "default",
  leftContent,
  rightContent,
  className = ""
}: BaseHeaderProps) {
  const variantStyles = {
    default: "w-full", // No background, used for dashboard pages
    editor: "bg-white border-b border-gray-200" // White background with border for editor pages
  };

  const containerStyles = {
    default: "py-0", // More padding for dashboard
    editor: "h-12 md:h-16" // Fixed height for editor
  };

  return (
    <header className={`relative z-50 ${variantStyles[variant]} ${className}`} style={{ isolation: 'isolate', paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="max-w-full mx-auto px-2 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center ${containerStyles[variant]}`}>
          {leftContent}
          {rightContent}
        </div>
      </div>
    </header>
  );
}

/**
 * Logo component for consistent logo rendering across headers
 */
export function HeaderLogo({ href = "/dashboard" }: { href?: string }) {
  return (
    <Link href={href} className="flex h-10 min-w-0 items-center overflow-hidden md:h-[60px]">
      <Image
        src="/assets/logo.svg"
        alt="PFPMakerAI Logo"
        width={310}
        height={75}
        priority
        className="h-10 w-auto max-w-full object-contain object-left md:h-[60px]"
      />
    </Link>
  );
}

/**
 * Back button component for editor headers
 * Checks for unsaved work before navigating
 */
export function HeaderBackButton({ href = "/dashboard", onClick }: { href?: string; onClick?: () => void }) {
  const router = useRouter();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // Check if there's an unsaved image warning handler
    const hasUnsavedImage = (window as any).__editorHasUnsavedImage?.();
    const showWarning = (window as any).__editorShowNavigationWarning;

    if (hasUnsavedImage && showWarning) {
      showWarning();
    } else if (onClick) {
      onClick();
    } else {
      router.push(href);
    }
  };

  return (
    <Link
      href={href}
      onClick={handleClick}
      className="flex items-center text-gray-500 hover:text-gray-700"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
      <span className="ml-2 md:ml-4 text-lg md:text-xl font-bold text-gray-900 truncate font-merriweather">Back</span>
    </Link>
  );
}

/**
 * Title component for editor headers (deprecated - now part of HeaderBackButton)
 */
export function HeaderTitle({ title }: { title: string }): null {
  return null;
}
